import { useEffect, type ReactNode } from 'react';

export function StatusPill({ label, colour }: { label?: string | null; colour?: string | null }) {
  if (!label) return null;
  return (
    <span className="pill" style={{ background: colour || '#9e9e9e' }}>
      {label}
    </span>
  );
}

/** A record shown over its list, so the board stays in place behind it. */
export function Panel({ title, sub, actions, onClose, children }: {
  title: ReactNode;
  sub?: ReactNode;
  actions?: ReactNode;
  onClose: () => void;
  children: ReactNode;
}) {
  useEscape(onClose);
  return (
    <>
      <div className="panel-shade" onClick={onClose} />
      <section className="panel" role="dialog" aria-modal="true">
        <div className="panel-head">
          <div className="grow">
            <h2 style={{ margin: 0 }}>{title}</h2>
            {sub && <div className="muted small">{sub}</div>}
          </div>
          {actions}
          <button className="ghost" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <div className="panel-body">{children}</div>
      </section>
    </>
  );
}

export function Modal({ title, onClose, footer, children }: {
  title: ReactNode;
  onClose: () => void;
  footer?: ReactNode;
  children: ReactNode;
}) {
  useEscape(onClose);
  return (
    <div className="modal-shade" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" role="dialog" aria-modal="true">
        <div className="modal-head">
          <h2 className="grow" style={{ margin: 0 }}>
            {title}
          </h2>
          <button className="ghost" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>
  );
}

function useEscape(fn: () => void) {
  useEffect(() => {
    const h = (e: KeyboardEvent) => e.key === 'Escape' && fn();
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [fn]);
}

export function Pager({ page, pageSize, total, onPage }: {
  page: number;
  pageSize: number;
  total: number;
  onPage: (p: number) => void;
}) {
  const from = total === 0 ? 0 : page * pageSize + 1;
  const to = Math.min(total, (page + 1) * pageSize);
  return (
    <div className="pager">
      {from}–{to} of {total.toLocaleString()}
      <button className="small" disabled={page === 0} onClick={() => onPage(page - 1)}>
        ‹ Prev
      </button>
      <button className="small" disabled={to >= total} onClick={() => onPage(page + 1)}>
        Next ›
      </button>
    </div>
  );
}

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="field">
      <span>{label}</span>
      {children}
    </label>
  );
}

export function ErrorNote({ error }: { error: string | null }) {
  return error ? <div className="error">{error}</div> : null;
}
